import { useEffect, useMemo, useRef, useState } from 'react';
import Modal from '@/components/ui/Modal';
import * as adminService from '@/services/adminService';

const emptyForm = {
  cityId: '',
  name: '',
  description: '',
  color: '#4A90D9',
  imageUrl: '',
  isActive: true,
};

const fieldClass = 'w-full rounded-lg border border-fg/10 bg-black/25 px-3 py-2 text-sm text-fg outline-none focus:border-accent';
const labelClass = 'text-xs font-bold uppercase tracking-[0.16em] text-fg/40';

export default function AdminZonesPage() {
  const [zones, setZones] = useState([]);
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [aiText, setAiText] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const aiTextRef = useRef(null);
  const cityById = useMemo(() => new Map(cities.map((city) => [city.id, city])), [cities]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [zonesRes, citiesRes] = await Promise.all([
        adminService.zones.list({ limit: 100 }),
        adminService.cities.list({ limit: 100 }),
      ]);
      const nextCities = citiesRes.data.data || [];
      setZones(zonesRes.data.data || []);
      setCities(nextCities);
      setForm((current) => ({ ...current, cityId: current.cityId || nextCities[0]?.id || '' }));
      setError('');
    } catch {
      setError('Não foi possível carregar as zonas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setForm({ ...emptyForm, cityId: cities[0]?.id || '' });
    setError('');
    setNotice('');
    setAiText('');
    setAiError('');
  };

  const applyAiSuggestion = async () => {
    const text = aiText.trim();
    if (!text) return;
    setAiLoading(true);
    setAiError('');
    try {
      const res = await adminService.aiSuggestZone(text);
      const suggestion = res.data.data || {};
      setForm((current) => ({
        ...current,
        ...(suggestion.name && { name: suggestion.name }),
        ...(suggestion.description && { description: suggestion.description }),
        ...(suggestion.color && { color: suggestion.color }),
      }));
    } catch (err) {
      setAiError(err.response?.data?.message || 'Erro ao contactar a IA. Tenta novamente.');
    } finally {
      setAiLoading(false);
    }
  };

  const openCreateModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const editZone = (zone) => {
    setEditingId(zone.id);
    setForm({
      cityId: zone.cityId || '',
      name: zone.name || '',
      description: zone.description || '',
      color: zone.color || '#4A90D9',
      imageUrl: zone.imageUrl || '',
      isActive: Boolean(zone.isActive),
    });
    setModalOpen(true);
  };

  const saveZone = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setNotice('');
    try {
      if (editingId) {
        await adminService.zones.update(editingId, form);
        setNotice('Zona atualizada com sucesso.');
      } else {
        await adminService.zones.create(form);
        setNotice('Zona criada com sucesso.');
      }
      await loadData();
      setModalOpen(false);
      resetForm();
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível guardar a zona.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (zone) => {
    try {
      await adminService.zones.update(zone.id, { isActive: !zone.isActive });
      await loadData();
    } catch {
      setError('Não foi possível alterar o estado da zona.');
    }
  };

  const removeZone = async (zone) => {
    if (!window.confirm(`Remover a zona "${zone.name}"?`)) return;
    try {
      await adminService.zones.remove(zone.id);
      await loadData();
    } catch {
      setError('Não foi possível remover a zona.');
    }
  };

  return (
    <section className="min-h-full">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-accent/70">Território</p>
          <h1 className="mt-1 font-serif text-3xl font-black text-accent">Gestão de Zonas</h1>
          <p className="mt-2 max-w-2xl text-sm text-fg/55">Organiza as áreas internas de cada cidade usadas pelo mapa e missões.</p>
        </div>
        <button type="button" onClick={openCreateModal} className="rounded-lg border border-accent/40 bg-accent/10 px-4 py-2 text-sm font-bold text-accent hover:bg-accent/20">
          Nova zona
        </button>
      </div>

      {(error || notice) && (
        <div className={`mt-5 rounded-lg border p-4 text-sm font-bold ${error ? 'border-energy/40 bg-energy/10 text-energy' : 'border-forest-light/40 bg-forest-light/10 text-forest-light'}`}>
          {error || notice}
        </div>
      )}

      <section className="mt-6 rounded-lg border border-fg/10 bg-fg/[0.04]">
        <div className="flex items-center justify-between border-b border-fg/10 p-4">
          <h2 className="font-serif text-xl font-black text-accent">Zonas registadas</h2>
          <span className="rounded-full border border-fg/10 bg-black/20 px-3 py-1 text-xs font-bold text-fg/55">{loading ? 'A carregar...' : `${zones.length} zona(s)`}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="text-xs uppercase tracking-[0.16em] text-fg/35">
              <tr>
                <th className="px-4 py-3">Zona</th>
                <th className="px-4 py-3">Cidade</th>
                <th className="px-4 py-3">Cor</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {!loading && zones.length === 0 && <tr><td className="px-4 py-6 text-fg/45" colSpan="5">Nenhuma zona registada.</td></tr>}
              {zones.map((zone) => (
                <tr key={zone.id}>
                  <td className="px-4 py-3"><div className="font-bold text-fg">{zone.name}</div><div className="text-xs text-fg/40">{zone.description || 'Sem descrição'}</div></td>
                  <td className="px-4 py-3 text-fg/60">{cityById.get(zone.cityId)?.name || '-'}</td>
                  <td className="px-4 py-3"><span className="inline-flex items-center gap-2 text-fg/60"><span className="h-4 w-4 rounded-full border border-fg/20" style={{ backgroundColor: zone.color }} />{zone.color}</span></td>
                  <td className="px-4 py-3">
                    <button type="button" onClick={() => toggleActive(zone)} className={`rounded-full border px-3 py-1 text-xs font-bold ${zone.isActive ? 'border-forest-light/30 bg-forest-light/10 text-forest-light' : 'border-fg/15 bg-fg/5 text-fg/45'}`}>
                      {zone.isActive ? 'Ativa' : 'Inativa'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button type="button" onClick={() => editZone(zone)} className="mr-2 rounded-lg border border-ocean-light/30 px-3 py-1.5 text-xs font-bold text-ocean-light hover:bg-ocean-light/10">Editar</button>
                    <button type="button" onClick={() => removeZone(zone)} className="rounded-lg border border-energy/30 px-3 py-1.5 text-xs font-bold text-energy hover:bg-energy/10">Remover</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} className="max-w-5xl">
        <div className="rounded-lg border border-accent/25 bg-background p-5 shadow-gold">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-accent/60">Território</p>
              <h2 className="font-serif text-2xl font-black text-accent">{editingId ? 'Editar zona' : 'Nova zona'}</h2>
            </div>
            <button type="button" onClick={() => setModalOpen(false)} className="rounded-lg border border-fg/10 px-3 py-1.5 text-sm font-bold text-fg/60 hover:bg-fg/10">Fechar</button>
          </div>
          <div className="mt-4 rounded-lg border border-ocean-light/20 bg-ocean-light/5 p-3">
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-ocean-light/70">Sugestão com IA</p>
            <textarea
              ref={aiTextRef}
              className="w-full resize-none rounded-lg border border-fg/10 bg-black/25 px-3 py-2 text-sm text-fg outline-none focus:border-ocean-light placeholder:text-fg/30"
              rows={3}
              placeholder="Descreve a zona em texto livre... ex: &quot;Zona histórica do centro da cidade, com edifícios do século XVIII e igrejas barrocas.&quot;"
              value={aiText}
              onChange={(e) => setAiText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) applyAiSuggestion(); }}
            />
            {aiError && <p className="mt-1 text-xs font-bold text-energy">{aiError}</p>}
            <button
              type="button"
              onClick={applyAiSuggestion}
              disabled={aiLoading || !aiText.trim()}
              className="mt-2 rounded-lg border border-ocean-light/40 bg-ocean-light/10 px-4 py-2 text-xs font-bold text-ocean-light hover:bg-ocean-light/20 disabled:opacity-50"
            >
              {aiLoading ? 'A gerar sugestão...' : 'Preencher com IA'}
            </button>
            <p className="mt-1 text-xs text-fg/30">Ctrl+Enter para enviar · Os campos preenchidos pela IA podem ser editados antes de guardar.</p>
          </div>

          <form className="mt-5 grid gap-3" onSubmit={saveZone}>
            <label className="grid gap-1"><span className={labelClass}>Cidade</span><select className={fieldClass} value={form.cityId} onChange={(e) => setForm({ ...form, cityId: e.target.value })} required>{cities.map((city) => <option key={city.id} value={city.id}>{city.name}</option>)}</select></label>
            <label className="grid gap-1"><span className={labelClass}>Nome</span><input className={fieldClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></label>
            <label className="grid gap-1"><span className={labelClass}>Descrição</span><textarea className={`${fieldClass} min-h-24 resize-y`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
            <label className="grid gap-1"><span className={labelClass}>Cor da zona</span><div className="grid grid-cols-[56px_1fr] gap-3"><input className="h-10 w-full rounded-lg border border-fg/10 bg-black/25 p-1" type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} /><input className={fieldClass} value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} /></div></label>
            <label className="grid gap-1"><span className={labelClass}>Imagem URL</span><input className={fieldClass} value={form.imageUrl} onChange={(e) => setForm({ ...form, imageUrl: e.target.value })} /></label>
            <label className="flex items-center gap-2 rounded-lg border border-fg/10 bg-black/15 p-3 text-sm font-bold text-fg/70"><input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />Ativa</label>
            <button type="submit" disabled={saving} className="rounded-lg bg-accent px-4 py-3 font-serif font-black text-fg shadow-gold disabled:opacity-60">{saving ? 'A guardar...' : editingId ? 'Guardar alterações' : 'Criar zona'}</button>
          </form>
        </div>
      </Modal>
    </section>
  );
}
